// « Des personnes que vous connaissez peut-être » — la carte des premiers jours.
//
// ─────────────────────────────────────────────────────────────────────────────
// LA FUITE QU'ELLE BOUCHE
//
// Un nouveau venu arrive avec zéro ami. Son fil lui montre le fondateur et la
// page KOINONIA (voir `js/decouverte.js`), ce qui vaut mieux qu'un écran blanc
// — mais ce n'est pas une communauté, c'est une vitrine. Tant qu'il n'a
// personne, il n'a aucune raison de revenir.
//
// Or l'application SAIT déjà à qui le présenter : chaque fiche porte une église
// et une ville. Quelqu'un de la même église est, à Parakou, quelqu'un qu'il
// croise le dimanche. Il n'y a rien à collecter de plus, rien à calculer de
// savant : il suffit de le lui montrer.
//
// ⚠️ ELLE DISPARAÎT D'ELLE-MÊME. Dès que le membre a un ami, la carte ne
// s'affiche plus. C'est une carte d'accueil, pas un module permanent — un
// réseau qui suggère éternellement des inconnus finit par ressembler à un
// annuaire, et l'annuaire existe déjà, dans son propre écran.
//
// ⚠️ ELLE NE CONTOURNE AUCUN RÉGLAGE. La liste vient de `Store.users.search`,
// que l'enveloppe filtre déjà : qui a décoché « Apparaître dans l'annuaire »
// n'y est pas, ici pas plus qu'ailleurs.

import { useEffect, useState, useMemo } from "react";
import Store, { etatAmitie } from "./../store.js";
import { suggererAmis, expliquer } from "./../algorithmes.js";
import Avatar from "./Avatar.jsx";
import Certifie from "./Certifie.jsx";
import BoutonsAmitie from "./BoutonsAmitie.jsx";
import { useSignaux } from "./hooks.js";

/* Six. Trois lignes sur un téléphone : assez pour donner le choix, trop peu
   pour repousser les publications hors de l'écran — c'est le même arbitrage
   que la liste des abonnés d'une page. */
const COMBIEN = 6;

/* ⚠️ LE CLASSEMENT EST CELUI DE LA MAISON, PAS LE MIEN.
   Cette carte triait d'abord à la main : même église, puis même ville, puis le
   reste. Ça marchait, mais ça faisait deux classements différents dans la même
   application — celui-ci et celui de l'annuaire — qui auraient divergé au
   premier réglage changé.
   `suggererAmis` fait mieux et le fait déjà : il compte les AMIS EN COMMUN
   (sous racine carrée — le premier ami commun est une information, le vingtième
   n'en est plus une), l'église, la ville, les groupes partagés, les réactions
   échangées. Et il rend la RAISON en toutes lettres, qu'on affiche telle quelle
   sous le nom : « 3 amis en commun », « Même église : ICC Parakou ».
   Le nombre d'amis en commun ne coûte aucune lecture de plus : `ctx.signaux` le
   tient déjà à jour pour l'annuaire. */

/**
 * La ligne sous le nom : pourquoi cette personne est proposée.
 *
 * ⚠️ LES AMIS EN COMMUN PASSENT DEVANT, même quand l'algorithme leur donne
 * moins de points que l'église. La raison est locale à Koinonia : la communauté
 * entière sort de l'ICC Parakou, donc « Même église » est vrai de TOUT LE
 * MONDE — c'est une phrase qui n'apprend rien et ne distingue personne.
 * « 3 amis en commun », si. On ne touche pas au CLASSEMENT pour autant : c'est
 * l'algorithme qui décide de l'ordre, on ne choisit ici que ce qu'on en dit.
 */
function raisonAffichee(raisons, u) {
  const communs = (raisons || []).find((r) => r.cle === "amis-communs");
  if (communs) return communs.texte;
  return (
    expliquer(raisons, 1)[0] || [u.eglise, u.ville].filter(Boolean).join(" · ") || "Membre"
  );
}

export default function SuggestionsMembres({ ctx, relations }) {
  const [membres, setMembres] = useState([]);
  const [ecartes, setEcartes] = useState(() => new Set());
  // Le voisinage — les amis de mes amis — arrive après coup, en plusieurs fois.
  // Sans cet abonnement, la carte resterait sur le premier calcul et
  // n'annoncerait jamais le moindre ami en commun.
  const versionSignaux = useSignaux(ctx.signaux);

  /* ⚠️ CINQ AMIS, ET NON « AU MOINS UN ».
     La carte se retirait dès la première amitié. C'était trop tôt, pour une
     raison qui ne saute pas aux yeux : les AMIS EN COMMUN sont ce qu'elle a de
     meilleur à dire — « 3 amis en commun » vaut dix fois « même ville » — et
     quelqu'un qui n'a aucun ami n'en a aucun en commun avec personne. En
     partant à un, elle disparaissait précisément à l'instant où elle devenait
     utile.
     On la garde donc les premiers jours, jusqu'à cinq amitiés. Au-delà, le fil
     se remplit tout seul et l'annuaire prend le relais dans son propre écran.

     Et on ne demande l'annuaire QUE si elle a une raison d'exister : sans ce
     garde-fou, chaque ouverture du fil paierait deux cents lectures pour une
     carte que personne ne verra. */
  const combienDAmis = (relations || []).filter((r) => r.etat === "acceptee").length;
  const aDesAmis = combienDAmis >= 5;

  useEffect(() => {
    if (aDesAmis) return;
    let vivant = true;
    Store.users
      .search("")
      .then((liste) => vivant && setMembres(liste.filter((u) => u.uid !== ctx.moi.uid)))
      .catch(() => {});
    return () => {
      vivant = false;
    };
  }, [aDesAmis, ctx.moi.uid]);

  const proposes = useMemo(() => {
    if (!ctx.signaux) return [];
    // Ceux à qui on a déjà écrit — demande envoyée ou reçue — n'ont plus rien à
    // faire ici : la carte doit se vider à mesure qu'on l'utilise.
    const dejaEnJeu = new Set(
      (relations || []).flatMap((r) => (r.membres || []).filter((m) => m !== ctx.moi.uid))
    );
    const candidats = membres.filter((u) => !dejaEnJeu.has(u.uid) && !ecartes.has(u.uid));
    const classes = suggererAmis(candidats, ctx.signaux.instantane(), { combien: COMBIEN });
    /* ⚠️ LE REPLI COMPLÈTE, IL NE REMPLACE PAS.
       `suggererAmis` écarte tout ce qui n'atteint pas son score minimum. Dans
       une communauté toute neuve — personne n'a d'ami, donc aucun ami en
       commun, aucun groupe partagé, aucune réaction échangée — il peut ne
       rendre presque personne. Or c'est PRÉCISÉMENT le moment où cette carte
       sert.

       ⚠️ C'ÉTAIT UN TOUT OU RIEN, et cela se voyait : `if (classes.length)`
       rendait le classement DÈS QU'IL CONTENAIT UN SEUL NOM, et le repli ne
       jouait jamais. Un membre au-dessus du score, et la carte n'en montrait
       qu'un — alors que vingt autres attendaient. Signalé par Josaphat le
       18 août 2026 : « pourquoi dans la suggestion d'amis, c'est un seul ami
       qui est affiché ».

       Les classés passent devant, avec leur raison ; les autres suivent, sans
       raison affichée. Mieux vaut proposer sans savoir dire pourquoi que ne
       rien proposer. */
    if (classes.length >= COMBIEN) return classes;
    const dejaCites = new Set(classes.map((c) => c.membre.uid));
    return classes.concat(
      candidats
        .filter((u) => !dejaCites.has(u.uid))
        .slice(0, COMBIEN - classes.length)
        .map((membre) => ({ membre, raisons: [] }))
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [membres, relations, ecartes, ctx.moi, versionSignaux]);

  if (aDesAmis || !proposes.length) return null;

  return (
    <section className="carte suggestions">
      <h2 className="suggestions__titre">Des personnes que vous connaissez peut-être</h2>
      <p className="petit texte-doux suggestions__aide">
        Ajoutez quelques membres pour que votre fil se remplisse.
      </p>
      <div className="suggestions__liste">
        {proposes.map(({ membre: u, raisons }) => (
          <div className="membre" key={u.uid}>
            <a className="membre__qui" href={`#/profil/${u.uid}`}>
              <Avatar membre={u} taille={46} />
              <span>
                <strong>
                  {u.nom}
                  <Certifie membre={u} taille={13} />
                </strong>
                <span className="petit texte-doux">{raisonAffichee(raisons, u)}</span>
              </span>
            </a>
            <span className="membre__actions">
              <BoutonsAmitie
                etat={etatAmitie(relations, ctx.moi.uid, u.uid)}
                uid={u.uid}
                nom={u.nom}
                ctx={ctx}
              />
              {/* Écarter quelqu'un est un droit, et il ne coûte rien : la carte
                  est une proposition, pas une liste de devoirs. */}
              <button
                className="btn btn--fantome btn--petit"
                type="button"
                aria-label={`Ne plus proposer ${u.nom}`}
                onClick={() => setEcartes((s) => new Set(s).add(u.uid))}
              >
                ✕
              </button>
            </span>
          </div>
        ))}
      </div>
    </section>
  );
}
